import { Timer } from '@repo/libs/Timer';
import { systemTimer } from '@repo/libs/SystemTimer';
import { useEffect, useEffectEvent } from 'react';

/**
 * Runs `callback` once after `delayMs`; `undefined` disarms it. A new `delayMs`
 * restarts the wait.
 *
 * A changed `restartKey` also restarts it, so a toast replaced by one of the
 * same duration gets its full time.
 *
 * The `Timer` is injected: a spec passes `new FakeTimer()` and calls
 * `act(() => timer.advance(ms))`. Hooks above forward their own `timer`.
 */
export type UseTimeout = (
  params: {
    callback: () => void;
    delayMs: number | undefined;
    restartKey?: number;
  },
  timer?: Timer,
) => void;

export const useTimeout: UseTimeout = (
  { callback, delayMs, restartKey },
  timer = systemTimer,
) => {
  // Runs the newest `callback` without a new one restarting the wait.
  const onElapsed = useEffectEvent(() => callback());

  useEffect(
    function startTimeout() {
      if (delayMs === undefined) return;

      return timer.setTimeout(function runCallback() {
        onElapsed();
      }, delayMs);
    },
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- `restartKey` is a dependency for its identity alone: a change is the caller asking for a fresh wait.
    [delayMs, restartKey, timer],
  );
};
